import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';

import { getLocations } from '../api/services/Locations';
import * as Constants from '../constants';

interface UseAccLocOptionsHook {
  debounceHandleQuery: (query: string) => void;
  locations: AccountLocation[];
  isLoading: boolean;
}

export const useAccountLocationsSearch = (): UseAccLocOptionsHook => {
  const [query, setQuery] = useState(null);
  const [locations, setLocations] = useState<AccountLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const abortController = useRef<AbortController>();

  const debounceHandleQuery = useMemo(() => debounce(handleQuery, Constants.DEBOUNCE_TIME), []);

  useEffect(() => {
    return () => {
      debounceHandleQuery.cancel();
    };
  }, [query]);

  function handleQuery(newQuery) {
    if (abortController.current?.signal.aborted) {
      return;
    }
    if (!newQuery) {
      setIsLoading(true);
      setLocations([]);
      setQuery(null);
    }

    if (newQuery !== query && newQuery.length >= Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      setIsLoading(true);
      setLocations([]);
      setQuery(newQuery);
    }
  }

  useEffect(() => {
    abortController.current = new AbortController();

    const fetchLocations = async () => {
      const response = await getLocations({ q: query }, abortController.current?.signal);

      if (response?.type === 'success') {
        setLocations(response.data.locations);
        setIsLoading(false);
      } else if (!abortController.current?.signal.aborted) {
        setIsLoading(false);
      }
    };

    fetchLocations();

    return () => abortController.current?.abort();
  }, [query]);

  return {
    debounceHandleQuery,
    locations,
    isLoading,
  };
};
